import { LoadingButton } from "@/shadcnComponents/ui/loading-button";
import {
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Tooltip,
} from "@/shadcnComponents/ui/tooltip";
import { ChevronRightIcon, Info, WalletIcon } from "lucide-react";
import mixpanel from "mixpanel-browser";
import { BiSync } from "react-icons/bi";
import { BudgetGaugeChart } from "../Components/Components";
import { useMemo } from "react";
import { format } from "date-fns";
import { PerformanceExpandableTable } from "../Components/LiveCampaignComponents/PerformanceComponent";
import { Button } from "../../../../../shadcnComponents/ui/button";
import {
  LiveCampaignData,
  OverallPerformanceData,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";
import { Progress } from "@/shadcnComponents/ui/progress";

export interface BudgetInfo {
  total: number;
  spent: number;
}

interface LiveCampaignPerformanceProps {
  collabId: string;
  collabData: LiveCampaignData;
  lastPostTrackingTime: Date;
  loadingTracking: boolean;
  overallPerformanceData: OverallPerformanceData;
  selectedInfluencers: SelectedInfluencer[];
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  handleUpdateAllPostTracking: (collabId: string) => Promise<void>;
  discountedPrice: number;
  handleTrackPost;
  handleViewReport: () => void;
}

export const LiveCampaignPerformance = ({
  collabId,
  collabData,
  lastPostTrackingTime,
  loadingTracking,
  overallPerformanceData,
  selectedInfluencers,
  setCurrentStep,
  handleUpdateAllPostTracking,
  discountedPrice,
  handleTrackPost,
  handleViewReport,
}: LiveCampaignPerformanceProps) => {
  const budget: BudgetInfo = useMemo(() => {
    const acceptedCampaignInfluencers = selectedInfluencers?.filter(
      (item) => item?.response === "accepted"
    );

    const expectedSpendAmount =
      collabData?.campaignInfo?.collabType === "fixed"
        ? selectedInfluencers?.reduce(
            (acc, item) => acc + item?.discountedApproxEarning,
            0
          )
        : parseFloat(collabData?.ROI?.safeExpense?.max?.toFixed(2) || "0");

    const addedFundsAmount = parseFloat(
      collabData?.Budget?.addedFundsAmount?.toFixed(2) || "0"
    );

    const total = (
      expectedSpendAmount +
      addedFundsAmount -
      (collabData?.campaignInfo?.collabType === "fixed"
        ? 0
        : discountedPrice || 0) +
      (collabData?.Budget?.anchorsCharges || 0)
    ).toFixed(2);

    let spent =
      selectedInfluencers?.filter((i) => i.trackingLink !== undefined)?.length >
      0
        ? (collabData?.Budget?.anchorsCharges /
            (acceptedCampaignInfluencers?.length || 1)) *
            (selectedInfluencers?.filter((i) => i.trackingLink !== undefined)
              ?.length || 1) || 0
        : 0;

    if (collabData?.campaignInfo?.collabType === "fixed") {
      selectedInfluencers
        ?.filter((i) => i.trackingLink !== undefined)
        ?.forEach((i) => (spent += i.discountedApproxEarning));
    } else {
      spent = parseFloat(overallPerformanceData?.spentAmount || "0");
    }

    return {
      total: parseFloat(total),
      spent: parseFloat(spent?.toFixed(2)),
    };
  }, [collabData, overallPerformanceData, discountedPrice]);
  return (
    <div className="flex w-full flex-col items-start gap-5 py-3 md:py-6 lg:py-12 px-[24px] bg-[#F9F9FB]">
      <div className="w-full flex justify-between">
        <h1 className="text-[#121212]  font-[Inter] text-[18px] not-italic font-semibold leading-[normal]">
          Live Campaign Performance
        </h1>

        <Button
          variant="outline"
          className="text-[12px] flex items-center gap-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          onClick={() => {
            setCurrentStep(6);
          }}
        >
          View All Analysis
          <ChevronRightIcon size={20} />
        </Button>
      </div>

      <div className="flex flex-col w-full items-start shrink-0 bg-[rgba(242,140,25,0.20)] pl-[17px] pr-2.5 py-2 rounded-lg border border-[#F28C19]">
        <span className="text-[#121212] text-base not-italic font-semibold leading-6">
          Note:
        </span>
        <span className="text-[color:var(--Natural-800,#424242)] text-xs not-italic font-normal leading-5 self-stretch">
          Your campaign stats will appear once influencers go live and sync
          their data. Public metrics like likes and comments can be synced
          anytime. For impressions and reach, click Sync Data, and the latest
          stats will be fetched shortly.
        </span>
      </div>
      {/* <div className="flex gap-1 items-center">
        <CalendarRange size={16} color="#DC2627" />
        <span className="text-[#6F6F6F] text-right font-[Inter] text-[12px] not-italic font-semibold leading-[20px]">
          {`${dateTimeFormat(
            collabData?.campaignInfo?.campaignStartDate
          )} - ${dateTimeFormat(collabData?.campaignInfo?.campaignEndDate)}`}
        </span>
      </div> */}
      <div className="flex gap-5 w-full">
        {/* overall performance */}
        <div className="border border-[#E7E8F7] rounded-lg bg-white flex flex-col gap-8 justify-between flex-1 p-4">
          <div className="flex justify-between items-center">
            <h3 className="text-[#121212] font-[Inter] text-base not-italic font-semibold leading-[normal]">
              Overall Summary
            </h3>
            <div className="flex gap-2">
              <div className="flex gap-1 items-center text-[#949090] text-right font-[Inter] text-xs not-italic font-normal leading-[normal]">
                {lastPostTrackingTime ? (
                  <>
                    <span>Last sync at </span>
                    {format(lastPostTrackingTime, "dd/MM/yyyy hh:mm a")}
                  </>
                ) : (
                  <span>Not Updated</span>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info size={12} color="#CEC3C4" />
                    </TooltipTrigger>
                    <TooltipContent className="w-[500px] text-center">
                      <p>
                        Public data (likes, comments, reposts) can be synced
                        directly. <br />
                        Private data (impressions, analytics) is synced by the
                        influencer.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <button
                className="flex gap-1 items-center rounded-md p-2 bg-[#F1F5F9] text-[#0F172A] font-[Inter] text-xs not-italic font-medium leading-[normal] hover:bg-[#E7E8F7]"
                onClick={() => {
                  mixpanel.track(
                    "Brand Live/Review Campaign Details: Performance section : Refresh Post Performance - clicked"
                  );
                  handleUpdateAllPostTracking(collabId);
                }}
              >
                <span>{loadingTracking ? "Syncing..." : "Sync Data"}</span>
                <LoadingButton
                  variant="ghost"
                  size="icon"
                  loading={loadingTracking}
                  className="p-0 m-0 size-4"
                >
                  {!loadingTracking && (
                    <>
                      <BiSync size={16} color="#3793FF" title="sync" />
                    </>
                  )}
                </LoadingButton>
              </button>
            </div>
          </div>
          <div className="flex justify-between">
            {[
              {
                title: "Total Reach",
                value:
                  overallPerformanceData?.totalImpressions > 0
                    ? overallPerformanceData?.totalImpressions?.toLocaleString()
                    : `-`,
                info: "The total reach of this campaign, as last synced by the influencer. It doesn't include repeat displays.",
              },
              {
                title: "Total Likes",
                value:
                  overallPerformanceData?.totalLikes > 0
                    ? overallPerformanceData?.totalLikes?.toLocaleString()
                    : `-`,
              },
              {
                title: "Total Comments",
                value:
                  overallPerformanceData?.totalComments > 0
                    ? overallPerformanceData?.totalComments?.toLocaleString()
                    : `-`,
              },
              {
                title: "Avg Engagement",
                value:
                  parseInt(overallPerformanceData?.avgEngagementPercentage) > 0
                    ? overallPerformanceData?.avgEngagementPercentage?.toLocaleString() +
                      "%"
                    : "-",
              },
            ].map((item, index, array) => (
              <>
                <div className="flex flex-col gap-2" key={index}>
                  <div className="flex gap-1 items-center">
                    <span className="text-[#5976B4] font-inter text-sm not-italic font-normal leading-[normal]">
                      {item?.title}
                    </span>
                    {item?.info && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info size={14} color="#CEC3C4" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm  text-wrap text-left">
                            <p>{item?.info}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="text-[#000] font-[Inter] text-lg not-italic font-bold leading-[normal]">
                    {item?.value}
                  </div>
                </div>
                {index < array?.length - 1 && (
                  <div
                    className="h-full w-px bg-[#E9E9E9]"
                    key={index + "_divider"}
                  />
                )}
              </>
            ))}
            {/* add fund button is hidden */}
            <Button
              onClick={() => setCurrentStep(6)}
              variant="destructive"
              className="hidden text-primary-foreground rounded-md px-4 py-2 items-center gap-2"
            >
              <WalletIcon className="h-5 w-5" />
              Add Funds
            </Button>
          </div>
        </div>
        {/* live influencers progress bar */}
        <div className="border border-[#E7E8F7] rounded-lg bg-white flex flex-col gap-8 justify-between p-4">
          <div className="px-2 w-full h-full flex items-center justify-start">
            <div className="flex flex-col gap-3 items-start justify-start">
              <h3 className="text-[#121212] font-[Inter] text-sm not-italic font-semibold leading-[normal]">
                Influencers Live
              </h3>

              <div className="flex flex-col gap-1 items-start justify-start">
                <span className="text-[#121212] text-md not-italic font-bold leading-[normal]">
                  {`${
                    selectedInfluencers?.filter(
                      (i) => i.trackingLink !== undefined
                    )?.length
                  } / ${
                    selectedInfluencers?.filter(
                      (item) => item?.response === "accepted"
                    )?.length
                  }`}
                </span>
                <Progress
                  className="w-[150px] h-3 [&>*]:bg-[var(--progress-color)]"
                  value={
                    (selectedInfluencers?.filter(
                      (i) => i.trackingLink !== undefined
                    )?.length /
                      selectedInfluencers?.filter(
                        (item) => item?.response === "accepted"
                      )?.length) *
                    100
                  }
                  style={{
                    "--progress-color": "#10b981", // green
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {/* Budget chart */}
        <div className="border border-[#E7E8F7] rounded-lg bg-white flex flex-col gap-8 justify-between p-4">
          <div className="px-10">
            <BudgetGaugeChart
              value={budget?.spent}
              max={budget?.total}
              label="Total Utilized Budget"
              colors={{
                background: "#e5e7eb",
                fill: budget?.spent >= budget?.total ? "#ef4444" : "#3b82f6",
                text: "#1f2937",
              }}
              thickness={10}
            />
          </div>
        </div>
      </div>

      <div className="border border-[#E7E8F7] rounded-lg bg-white w-full flex flex-col gap-[5px] p-5 pb-40">
        <PerformanceExpandableTable
          Influencers={selectedInfluencers?.filter(
            (item) => item?.brandApproval === "accepted"
          )}
          collabId={collabId}
          selectedInfluencers={selectedInfluencers}
          handleTrackPost={handleTrackPost}
          handleViewReport={handleViewReport}
        />
      </div>
    </div>
  );
};

// const engagementRate = (likes = 0, comments = 0, reposts = 0, followers) => {
//   if (!followers || followers === 0) return 0;
//   const totalEngagement = likes + comments + reposts;
//   const engagementRate = (totalEngagement / followers) * 100;
//   return engagementRate;
// };
